import { type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InlineLink } from 'InlineLink';
import { MailtoLink } from 'MailtoLink';
import { Modal } from 'Modal';
import { Text } from 'Text';
import { getSupportEmailAddress } from 'Cohorts';
import { formattedUserFacingMonthYearLong } from 'DateHelpers';
import { navigationHelper } from 'NavigationHelpers';
import { type CurrentUser, getCohort, getReapplicationDate } from 'Users';
import { LOCALE_PREFIX } from '../constants';

type DeferralModalProps = {
    currentUser: CurrentUser;
    // Present when the learner has a scholarship, which changes the first paragraph.
    hasScholarship: boolean;
    onClose: () => void;
};

export function DeferralModal({ currentUser, hasScholarship, onClose }: DeferralModalProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const { loadFaqUrl } = navigationHelper();
    const cohort = getCohort(currentUser);

    const admissionsEmail = cohort ? (getSupportEmailAddress(cohort, 'admissions') ?? '') : '';
    const reapplicationDate = getReapplicationDate(currentUser);

    /*
        A past reapplication date means reapplying is already open, so this copy would be wrong.
    */
    const showReapplyParagraph = !reapplicationDate || reapplicationDate > new Date();
    const reapplicationMonthYear = reapplicationDate
        ? formattedUserFacingMonthYearLong(reapplicationDate, false)
        : undefined;

    return (
        /*
            The close button is absolutely positioned in the header's right gutter, and the header
            reserves no room for it, so this title ran under it. px-14 clears the button on both
            sides, which keeps the centred title centred and works in RTL too.
        */
        <Modal
            titleKey={`${LOCALE_PREFIX}.deferral_modal_title`}
            size="lg"
            classNames={{ header: 'px-14 leading-tight' }}
            onClose={onClose}
        >
            <div className="flex flex-col gap-4 px-7.5 pb-7.5">
                <Text as="p" className="mb-0">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.${hasScholarship ? 'preserve_your_admission_and_scholarship' : 'preserve_your_admission'}`}
                        components={{
                            deferralLimitLink: (
                                <InlineLink onPress={() => loadFaqUrl('/help/what-does-it-mean-to-defer', '_blank')} />
                            ),
                        }}
                    />
                </Text>

                <Text as="p" className="mb-0">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.email_us_to_request_future_start_date`}
                        components={{ mail: <MailtoLink className="hover:text-primary" email={admissionsEmail} /> }}
                    />
                </Text>

                {showReapplyParagraph ? (
                    <Text as="p" className="mb-0">
                        {t(
                            `${LOCALE_PREFIX}.${reapplicationMonthYear ? 'reapply_at_different_time' : 'reapply_at_different_time_no_date'}`,
                            { reapplicationMonthYear: reapplicationMonthYear ?? '' },
                        )}
                    </Text>
                ) : null}

                <Text as="p" className="mb-0">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.refund_policy`}
                        components={{
                            refundPolicyLink: (
                                <InlineLink onPress={() => loadFaqUrl('/help/tuition-refund-policy', '_blank')} />
                            ),
                        }}
                    />
                </Text>
            </div>
        </Modal>
    );
}
